/**
 * Suspension-appeal API. Two endpoints, both authed:
 *  - POST /auth/me/appeal — kid files an appeal
 *  - GET /auth/me/appeal — fetch the kid's most recent appeal
 *    (open or resolved)
 *
 * The suspension banner reads from `getMyAppeal`; if `decision`
 * is set the banner shows the resolution copy. If the response is
 * null the kid sees "you can file an appeal" instead.
 */

import { AuthApiError, apiBaseUrl } from './authApi';

export type AppealDecision = 'upheld' | 'overturned';

export interface AppealStatus {
  id: string;
  message: string;
  decision: AppealDecision | null;
  decision_note: string | null;
  created_at: string;
  resolved_at: string | null;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const url = `${apiBaseUrl()}${path}`;
  const headers = new Headers(init.headers ?? {});
  if (init.body && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }
  const response = await fetch(url, { ...init, headers, credentials: 'include' });
  if (response.status === 204) return undefined as T;
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // Empty body — fall through.
  }
  if (!response.ok) {
    const detail =
      body && typeof body === 'object' && 'detail' in body && typeof body.detail === 'string'
        ? body.detail
        : null;
    throw new AuthApiError(
      response.status,
      detail,
      `Request to ${path} failed (${response.status}).`,
    );
  }
  return body as T;
}

export async function fileAppeal(message: string): Promise<AppealStatus> {
  return request<AppealStatus>('/auth/me/appeal', {
    method: 'POST',
    body: JSON.stringify({ message }),
  });
}

export async function getMyAppeal(): Promise<AppealStatus | null> {
  return request<AppealStatus | null>('/auth/me/appeal');
}
